import { useState, useEffect } from "react";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { trackPhoneCall } from "@/lib/gtag";
import soharLogo from "@/assets/sohar-logo-hq.webp";
import heroLogo from "@/assets/sohar-logo-hero-cream.png";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, lang, setLang, isRTL } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { label: t("nav.home"), href: "#hero" },
    { label: t("nav.projects"), href: "#projects" },
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => {
    if (lang === "ar") {
      setLang("en");
      navigate("/en");
    } else {
      setLang("ar");
      navigate("/");
    }
  };

  return (
    <nav
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 animate-nav-slide-down ${
        scrolled
          ? "bg-navy/95 backdrop-blur-xl shadow-2xl"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between py-4 px-6">
        <a href="#hero" className="flex items-center">
          <img
            src={heroLogo}
            alt="Sohar"
            className="h-10 md:h-12 w-auto object-contain drop-shadow-lg"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href.startsWith("/") ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-cream/80 hover:text-cream transition-colors font-medium text-sm"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="text-cream/80 hover:text-cream transition-colors font-medium text-sm"
              >
                {link.label}
              </a>
            )
          )}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-cream/80 hover:text-cream transition-colors text-sm font-medium"
          >
            <Globe size={16} />
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <a
            href="tel:+966115001577"
            onClick={trackPhoneCall}
            className="bg-cream text-navy font-bold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Phone size={16} />
            {t("nav.call")}
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-cream"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu - CSS animation */}
      <div
        className={`md:hidden bg-navy backdrop-blur-xl border-t border-cream/10 overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container mx-auto py-6 px-6 flex flex-col gap-4">
          {navLinks.map((link) =>
            link.href.startsWith("/") ? (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-cream/80 hover:text-cream transition-colors py-2 font-medium"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-cream/80 hover:text-cream transition-colors py-2 font-medium"
              >
                {link.label}
              </a>
            )
          )}
          <button
            onClick={() => { toggleLang(); setMobileOpen(false); }}
            className="flex items-center gap-2 text-cream/80 hover:text-cream transition-colors py-2 font-medium"
          >
            <Globe size={18} />
            {lang === "ar" ? "English" : "عربي"}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
